import React, { useEffect, useState } from "react";
import { contentHeader } from "../utils/header";
import Select from "react-select";
import axios from "axios";
import AnnualPreventionTrends from "../components/charts/AnnualPreventionTrends";
import PreventionByType from "../components/charts/PreventionByType";
import PreventionByMonth from "../components/charts/PreventionsByMonth";
import PreventionIncidenceByEntity from "../components/charts/PreventionIncidenceByEntity";

type Props = {};
type PreventionDataType = {
  labels: string[];
  values: number[];
};

type PreventionDataState = {
  byType: PreventionDataType;
  byEntity: PreventionDataType;
  byAgreementType: PreventionDataType;
  byTenureType: PreventionDataType;
  byPropertyType: PreventionDataType;
};

interface EvictionData {
  city: string;
  month: string;
  numberOfEvictions: number;
}

interface CityOption {
  label: string;
  value: string;
}

const Preventions = (props: Props) => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [tab, setTab] = useState<number>(1);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 11 }, (_, index) =>
    (currentYear - index).toString()
  );

  const [chartData, setChartData] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedYear, setSelectedYear] = useState<string>(
    currentYear.toString()
  );
  const [cities, setCities] = useState<CityOption[]>([]);
  const [selectedCity, setSelectedCity] = useState<CityOption | null>(null);
  const [preventionData, setPreventionData] = useState<PreventionDataState>({
    byType: { labels: [], values: [] },
    byEntity: { labels: [], values: [] },
    byAgreementType: { labels: [], values: [] },
    byTenureType: { labels: [], values: [] },
    byPropertyType: { labels: [], values: [] },
  });

  // Fetch cities from backend
  const fetchCities = async () => {
    try {
      const response = await axios.get<CityOption[]>(
        `${BASE_URL}/cities/`
      );
      setCities(response.data);
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  };
  const preventionTypes = [
    { key: "byType", label: "Prevention Type", endpoint: "preventions-by-preventions-type" },
    { key: "byEntity", label: "Entity Type", endpoint: "preventions-by-entity-type" },
    { key: "byAgreementType", label: "Agreement Type", endpoint: "preventions-by-agreement-type" },
    { key: "byTenureType", label: "Tenure Type", endpoint: "preventions-by-tenure-type" },
    { key: "byPropertyType", label: "Property Type", endpoint: "preventions-by-property-type" },
  ] as const;

  const fetchPreventions = async (year: string, city: string | null) => {
    try {
      setIsLoading(true);
      const queryParams = `year=${year}${city ? `&city=${city}` : ""}`;
      const headers = contentHeader();

      // Fetch all prevention types in parallel
      const responses = await Promise.all(
        preventionTypes.map(({ endpoint }) =>
          axios
            .get<{ labels?: string[]; values?: number[] }>(
              `${BASE_URL}/preventiondata/${endpoint}/?${queryParams}`,
              { headers }
            )
            .catch((error) => {
              console.error(`Error fetching ${endpoint}:`, error);
              return { data: { labels: [], values: [] } }; // Return empty structure on error
            })
        )
      );

      // Structure the fetched data properly
      const preventionData = preventionTypes.reduce(
        (acc, { key }, index) => ({
          ...acc,
          [key]: responses[index]?.data ?? { labels: [], values: [] },
        }),
        {} as PreventionDataState
      );

      setPreventionData(preventionData);

      // Generate chart data dynamically
      const chartData = preventionTypes.reduce((acc, { key }) => {
        const data = preventionData[key];

        if (!data?.labels?.length || !data?.values?.length) {
          return acc; // Skip if there's no data
        }

        acc[key] = {
          labels: data.labels,
          datasets: [
            {
              label: preventionTypes.find((t) => t.key === key)?.label || key,
              data: data.values,
              backgroundColor: "rgba(54, 162, 235, 0.5)",
            },
          ],
        };

        return acc;
      }, {} as Record<string, any>);

      setChartData(chartData);
    } catch (error) {
      console.error("Error fetching prevention data:", error);
    } finally {
      setIsLoading(false);
    }
  };


  const fetchPreventionsByAgreementType = async (year: string, city: string | null) => {
    try {
      setIsLoading(true);
      const queryParams = `year=${year}${city ? `&region=${city}` : ""}`;

      const headers = contentHeader();
      //console.log("Request Headers:", headers);

      const response = await axios.get(
        `${BASE_URL}/preventiondata/preventions-by-agreement-type/?${queryParams}`,
        { headers }
      );

      //console.log("API Response:", response.data);

      setChartData({
        labels: response.data.labels,
        datasets: [
          {
            label: "Preventions",
            data: response.data.values,
            backgroundColor: "rgba(54, 162, 235, 0.5)",
          },
        ],
      });
    } catch (error: any) {
      console.error("Error fetching prevention data:", error);

      if (error.response) {
        console.error("Error details:", error.response.status, error.response.data);
      }

      if (error.response?.status === 401) {
        console.error("Unauthorized - Check API authentication.");
      }
    } finally {
      setIsLoading(false);
    }
  };




  useEffect(() => {
    fetchCities();
    fetchPreventionsByAgreementType(selectedYear, selectedCity?.value || null);
    fetchPreventions(selectedYear, selectedCity?.value || null);
  }, [selectedYear, selectedCity]);

  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(event.target.value);
  };

  const handleCityChange = (option: CityOption | null) => {
    setSelectedCity(option);
  };

  const chartIds: Record<string, string> = {
    byTenureType: "my-chart-id-2",
    byEntity: "my-chart-id-5",
    byAgreementType: "my-chart-id-6",
    byPropertyType: "my-chart-id-7",
  };

  return (
    <div className="w-full">
      <p className="text-3xl font-bold mb-4">Preventions</p>
      <div className="grid md:grid-cols-2 sm:grid-cols-1 gap-4 rounded">
        <div className="md:col-span-2 sm:col-span-1">
          <div className=" border border-dark  rounded bg-dark text-white py-2 px-3">
            <label htmlFor="year-select">Select Year: </label>
            <select
              id="year-select"
              value={selectedYear}
              onChange={handleYearChange}
              style={{ marginRight: "20px" }}
              className="rounded px-2 py-[6px] bg-white text-gray border border-gray"
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>

            <label htmlFor="city-select">Select Regions: </label>
            <Select
              id="city-select"
              options={cities}
              value={selectedCity}
              onChange={handleCityChange}
              placeholder="All Regions"
              isClearable
              isSearchable
              styles={{
                container: (base) => ({
                  ...base,
                  display: "inline-block",
                  width: "200px",
                  color: "gray",
                }),
              }}
            />
          </div>
        </div>

        <div className="md:col-span-2 sm:col-span-1">
          <div className="flex gap-2">
            <p
              className={`px-2 py-1 cursor-pointer text-orange font-bold ${tab === 1 && "border-b"
                }`}
              onClick={() => setTab(1)}
            >
              Trends
            </p>
            <p
              className={`px-2 py-1 cursor-pointer text-orange font-bold ${tab === 3 && "border-b"
                }`}
              onClick={() => setTab(2)}
            >
              Prevention & Response Services
            </p>
          </div>
        </div>

        {tab === 1 && (
          <div className="md:col-span-2 sm:col-span-1 grid md:grid-cols-2 sm:grid-cols-1 gap-4">
            <div className="shadow-lg p-6 rounded mb-4">
              <p className="text-lg font-bold">Prevention Trends By Month</p>
              <PreventionByMonth
                selectedYear={selectedYear}
                selectedCity={selectedCity}
              />
            </div>

          </div>
        )}

        {tab === 2 && (
          <div className="md:col-span-2 sm:col-span-1 grid md:grid-cols-2 sm:grid-cols-1 gap-4">

            <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', padding: '20px' }}>
              <h3 style={{ marginBottom: '15px', color: '#444' }}>Prevention/Response Services</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Available Services</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      "Due diligence support",
                      "Tenure security support",
                      "Legal Assistance CDR (Mediation, Negotiation, Legal drafting and referral)",
                      "Legal Counselling",
                      "Information services",
                      "Advocacy (local and high level)",
                      "Eviction monitoring",
                      "Eviction Risk mapping and reporting"
                    ].map((service, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px', display: 'flex', alignItems: 'center' }}>
                          <span style={{ marginRight: '8px' }}>•</span>
                          {service}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default Preventions;